import React from 'react';
import { Box, Button, Divider, Stack } from '@mui/material';
import MyPageProfile from '../../@components/Profile';
import { SubContentProps } from '../LanguageSetting';
import { VisuallyHiddenInput } from '@/components/Board/CreateBoardModal/BoardForm/BoardFormField/DisplaySections/styled';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import { ProfileEditButton } from './style';
import useEditProfileProps from './@hooks/useEditProfileProps';
import FormProvider, {
  FormProviderProps,
} from '@/components/HookForm/FormProvider';
import Label from '@/components/@IntranetLibrary/Label';
import { FormGroup } from '@/components/Board/CreateBoardModal/BoardForm/BoardFormField/style';
import RHFTextField from '@/components/HookForm/RHFTextField';
import RHFTextArea from '@/components/HookForm/RHFTextarea';
import RHFSelect from '@/components/HookForm/RHFSelect';
import { SelectProps } from '@/components/@IntranetLibrary/Select';
import { ProfileForm } from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/EditProfile/@hooks/useProfileForm';
import EditIcon from '@/public/icons/edit.svg';
import { useMypageEventTracker } from '@/hooks/gtm/useGTMEventTracker';
import { MypageEventName } from '@/constant/gtm';
import { useTranslation } from 'react-i18next';

export type EditProfileProps = {
  readonly profileImageUrl: string;
  readonly isEditButtonDisabled: boolean;
  readonly editProfileImage: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => Promise<void>;

  readonly jobOptions: SelectProps['options'];
  readonly jobValue: string;
  readonly isJobListFetching: boolean;

  readonly nicknameHelperText: string;
  readonly nicknameHelperTextColor?: string;
  readonly nicknameOnChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  readonly nicknameOnClear: () => void;
  readonly nicknameHasError: boolean;

  readonly isCheckButtonDisabled: boolean;
  readonly isCheckButtonLoading: boolean;
  readonly checkDuplicateName: () => void;

  readonly isSubmitButtonDisabled: boolean;
  readonly isFormSubmitting: boolean;

  readonly formProps: FormProviderProps<ProfileForm>;
};

const EditProfile = (props: SubContentProps) => {
  const {
    nicknameHelperText,
    nicknameHelperTextColor,
    nicknameOnChange,
    nicknameOnClear,
    nicknameHasError,
    jobOptions,
    jobValue,
    isJobListFetching,
    editProfileImage,
    isEditButtonDisabled,
    profileImageUrl,
    formProps,
    isCheckButtonDisabled,
    isCheckButtonLoading,
    checkDuplicateName,
    isSubmitButtonDisabled,
    isFormSubmitting,
  } = useEditProfileProps(props);
  const { t } = useTranslation();

  const mypageEventTracker = useMypageEventTracker();

  const onClickEditProfileImage = () => {
    // GA Mypage - 9번
    mypageEventTracker(MypageEventName.clickEvent, {
      area_name: '프로필 편집',
      contents: undefined,
      status: undefined,
      description: '프로필 사진 변경',
    });
  };

  const onClickCheckDuplicateName = () => {
    checkDuplicateName();
    // GA Mypage - 10번
    mypageEventTracker(MypageEventName.clickEvent, {
      area_name: '프로필 편집',
      contents: '중복확인',
      status: undefined,
      description: '영어 이름 중복확인',
    });
  };

  const onClickSubmit = () => {
    // GA Mypage - 11번
    mypageEventTracker(MypageEventName.clickEvent, {
      area_name: '프로필 편집',
      contents: '저장하기',
      status: undefined,
      description: '프로필 저장하기',
    });
  };

  return (
    <>
      <MyPageProfile profileImageUrl={profileImageUrl}>
        <ProfileEditButton disableRipple component={'label'}>
          {isEditButtonDisabled ? (
            <Spinner theme={'black'} size={'16px'} />
          ) : (
            <EditIcon width={16} height={16} stroke={'black'} />
          )}
          <VisuallyHiddenInput
            type="file"
            hidden
            accept="image/jpg, image/jpeg, image/png"
            onClick={onClickEditProfileImage}
            onChange={editProfileImage}
            disabled={isEditButtonDisabled}
          />
        </ProfileEditButton>
      </MyPageProfile>
      <FormProvider {...formProps}>
        <Stack sx={{ padding: '28px 20px 24px' }}>
          <FormGroup gapsize={'s'}>
            <Label isRequired={false} title={t('MYPAGE.영어이름')} size={14} />
            <Stack direction={'row'} gap={'8px'} sx={{ width: '100%' }}>
              <Stack flex={1}>
                <RHFTextField
                  name={'nickname'}
                  error={nicknameHasError}
                  helperText={nicknameHelperText}
                  variant={'outlined'}
                  onChange={nicknameOnChange}
                  onClear={nicknameOnClear}
                  placeholder={'john'}
                  slotProps={{ htmlInput: { maxLength: 15 } }}
                  sx={{
                    flex: 1,
                    '& .MuiInputBase-root': {
                      fontSize: '14px',
                      lineHeight: '20px',
                    },
                    '& .MuiFormHelperText-root': {
                      color: nicknameHelperTextColor,
                    },
                  }}
                />
              </Stack>
              <Button
                variant={'contained'}
                size={'large'}
                onClick={onClickCheckDuplicateName}
                disabled={isCheckButtonDisabled}
                loading={isCheckButtonLoading}
              >
                {t('중복확인')}
              </Button>
            </Stack>
          </FormGroup>
          <FormGroup gapsize={'s'} sx={{ margin: '28px 0 24px' }}>
            <Label isRequired={false} title={t('MYPAGE.직무')} size={14} />
            <RHFSelect
              name="job"
              options={jobOptions}
              value={jobValue}
              disabled={isJobListFetching}
            />
          </FormGroup>
          <FormGroup gapsize={'s'}>
            <Label isRequired={false} title={t('MYPAGE.한줄 요약')} size={14} />
            <RHFTextField
              name={'summary'}
              variant={'outlined'}
              maxLength={40}
              slotProps={{ htmlInput: { maxLength: 40 } }}
              placeholder={'Portal Product Manager'}
              sx={{
                '& .MuiInputBase-root': {
                  fontSize: '14px',
                  lineHeight: '20px',
                },
              }}
            />
          </FormGroup>
          <FormGroup gapsize={'s'}>
            <Label isRequired={false} title={t('MYPAGE.내용')} size={14} />
            <RHFTextArea
              name={'content'}
              maxLength={300}
              minRows={3}
              placeholder={t(
                'MYPAGE.저의 주요 업무는 A, B, C입니다.\n요즘 진행중인 프로젝트는 ~를 위한 ~입니다.\n~가 필요할 때 저에게 연락주세요.',
              )}
            />
          </FormGroup>
        </Stack>
        <Divider />
        <Box sx={{ padding: '24px 20px 32px' }}>
          <Button
            variant={'contained'}
            size={'large'}
            type={'submit'}
            disabled={isSubmitButtonDisabled}
            loading={isFormSubmitting}
            fullWidth
            onClick={onClickSubmit}
          >
            {t('저장')}
          </Button>
        </Box>
      </FormProvider>
    </>
  );
};

export default EditProfile;
